/**
 * Authentication Service
 *
 * Fetches user context from the backend /auth/me endpoint.
 * The backend reads the HttpOnly access_token cookie and returns user claims.
 */

export interface UserContext {
  personId: string | null;
  email: string | null;
  resellerId: string | null;
  tag: string | null;
}

interface MeResponse {
  person_id: string;
  reseller_id?: string;
  email?: string;
  tag?: string;
  exp?: number;
  error?: string;
}

/* v8 ignore next */
const API_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

/**
 * AuthService — authenticates via backend /auth/me endpoint.
 *
 * The access_token cookie is HttpOnly so JS cannot read it directly.
 * Instead we call /auth/me with credentials:'include' and the backend
 * reads the cookie, validates it, and returns user claims.
 */
export class AuthService {
  private static userContext: UserContext | null = null;
  private static fetchPromise: Promise<UserContext | null> | null = null;

  /**
   * Fetch user context from the backend.
   * Returns null if not authenticated.
   */
  static async fetchMe(): Promise<UserContext | null> {
    // Deduplicate concurrent calls
    if (this.fetchPromise) {
      return this.fetchPromise;
    }

    this.fetchPromise = (async () => {
      try {
        const resp = await fetch(`${API_URL}/auth/me`, {
          credentials: 'include',
        });

        if (!resp.ok) {
          this.userContext = null;
          return null;
        }

        const data = (await resp.json()) as MeResponse;
        if (data.error) {
          this.userContext = null;
          return null;
        }

        this.userContext = {
          personId: data.person_id ?? null,
          email: data.email ?? null,
          resellerId: data.reseller_id ?? null,
          tag: data.tag ?? null,
        };

        return this.userContext;
      /* v8 ignore next 5 */
      } catch {
        this.userContext = null;
        return null;
      } finally {
        this.fetchPromise = null;
      }
    })();

    return this.fetchPromise;
  }

  /**
   * Check if user is authenticated (cached result from fetchMe).
   */
  static isAuthenticated(): boolean {
    return this.userContext !== null;
  }

  /**
   * Get cached user context. Call fetchMe() first.
   */
  static getUserContext(): UserContext {
    return this.userContext ?? {
      personId: null,
      email: null,
      resellerId: null,
      tag: null,
    };
  }

  static getPersonID(): string | null {
    return this.userContext?.personId ?? null;
  }

  static getDisplayName(): string {
    return this.userContext?.email ?? 'Guest';
  }

  /**
   * Clear cached context (e.g. on logout).
   */
  static clear(): void {
    this.userContext = null;
    this.fetchPromise = null;
  }
}
